// Dérogations, côté bureau (D-137, maquettes d1, d2, d4 ; D-146, maquettes l1 à l6 et w1 validées) : chaque dérogation
// en 3 étapes (« Les prévenir ») ou en 4 (« Demander leur accord », demande reçue), et le geste attendu.
//  - #/derogations : « Nouvelle demande », « Demande reçue » ; les changements FFBB sans dérogation (un bandeau, à valider
//    comme avant, D-117) ; deux onglets « Nos demandes » / « Demandes reçues » avec leur nombre et leurs « à faire » ; la
//    recherche (équipe, adversaire), qui passe sur « Toutes » ; les puces d'état avec compteurs (« À faire maintenant » par
//    défaut ; « À saisir sur FBI » en est un sous-ensemble) ; triées par date du match, groupées par week-end ; les
//    confirmées, refusées ou abandonnées restent jusqu'au match puis sont rangées le lendemain (« Rangées », consultables
//    et cherchables) ; les demandes des coachs (D-061) se reprennent ici ;
//  - version large (au-delà de 1000 px) : la liste et ses filtres à gauche, la fiche à droite (pour saisir dans FBI à côté) ;
//  - #/derogations/<id> : la fiche d'une dérogation (les étapes datées, le texte à reporter dans FBI) ;
//  - #/derogations/reglages : le délai « sans réponse » du club (2 à 7 jours ; un rappel, jamais un verrou).
// Les filtres ne laissent pas d'entrée d'historique (D-139) : ils restent le temps de la session.
import {
  clubStaffNames, clubUpcomingMatches, derogPre, derogPres, derogRangees, derogSeen, derogWaitDays, derogations, ffbbAlertsMine, matchesWithEvents,
  pendingChanges, setDerogWaitDays, type DerogPre, type MatchChange, type TeamMatch,
} from '../lib/api';
import { addDays, localDate } from '../domain/schedule';
import { currentOf, dayHm, derogChip, fbiNow, searchKey, weekendLabel, type DerogChip } from '../domain/derogation-suivi';
import { squadName, type Ctx } from './context';
import { $, $$, html, mount, openSheet, toast, type Html } from './dom';
import { shortDate, time } from './format';
import { coachLabel, competitionOf, openDerogPreSheet, type PrePrefill, type PreTarget } from './derogation-pre';
import { openRecueSheet } from './derogation-recue';
import { derogFiche, derogRow, infoOf, showHall, stepBar, wireDerogActions, type DerogInfo } from './derogation-track';
import { settingsPage } from './nav';
import './derogations.css';

const when = (iso: string | null) => (iso ? `${shortDate(localDate(new Date(iso)))} à ${time(new Date(iso))}` : '—');
const two = (n: number) => String(n).padStart(2, '0');
const UUID = /^[0-9a-f-]{36}$/i;
const WIDE = '(min-width: 1000px)';

/** Le club du bureau affiché. */
const clubOf = (ctx: Ctx) => ctx.adminOf.find(c => c.id === ctx.club?.id) ?? ctx.adminOf[0] ?? ctx.club;

// --- Les filtres (sans entrée d'historique, D-139 : ils restent le temps de la session) --------------------------------
type Chip = DerogChip | 'fbi' | 'all' | 'stored';
const OUT_CHIPS: [Chip, string][] = [['todo', 'À faire maintenant'], ['wait', 'En attente de réponse'], ['fbi', 'À saisir sur FBI'], ['ffbb', 'En attente de la FFBB'],
  ['ok', 'Confirmées'], ['ko', 'Refusées ou abandonnées'], ['all', 'Toutes']];
const IN_CHIPS: [Chip, string][] = [['todo', 'À faire maintenant'], ['theirs', 'Saisie FBI par eux'], ['ffbb', 'En attente de la FFBB'], ['ok', 'Confirmées'],
  ['ko', 'Refusées ou abandonnées'], ['all', 'Toutes']];
type Tab = 'out' | 'in';
const state: { club: string; tab: Tab; chips: Record<Tab, Chip>; before: Record<Tab, Chip | null>; q: string; sort: 'match' | 'asked'; sel: string | null } =
  { club: '', tab: 'out', chips: { out: 'todo', in: 'todo' }, before: { out: null, in: null }, q: '', sort: 'match', sel: null };
let wideWatch: { mq: MediaQueryList; fn: () => void } | null = null;

/** Ce que la page et la fiche chargent : les dérogations du club, leurs matchs, les prénoms, le délai. */
async function load(ctx: Ctx, clubId: string, only?: DerogPre[]) {
  const [all, names, wait] = await Promise.all([
    only ? Promise.resolve(only) : derogPres().catch(() => [] as DerogPre[]),
    clubStaffNames(clubId).catch(() => new Map<string, string>()), derogWaitDays(clubId).catch(() => 3)]);
  // D-146 : une demande retirée reste (« Refusées ou abandonnées ») tant qu'elle est la plus récente de son match.
  const pres = all.filter(p => p.club_id === clubId);
  const ids = [...new Set(pres.flatMap(p => [p.event_id, ...(p.pair_event_id ? [p.pair_event_id] : [])]))];
  const matches = await matchesWithEvents(ids).catch(() => [] as TeamMatch[]);
  const teamName = (id: string) => ctx.teams.find(t => t.id === id)?.name ?? '';
  const team = (m: TeamMatch) => squadName(ctx, m.squad_id) ?? teamName(m.events.team_id);
  const clubName = ctx.clubs.find(c => c.id === clubId)?.name ?? ctx.club?.name ?? 'Le club';
  // « Marc, coach SF1 » : une fois par équipe.
  const coaches = new Map<string, Promise<string | null>>();
  const coachOf = (m: TeamMatch | undefined) => {
    if (!m) return Promise.resolve(null);
    const k = `${m.events.team_id}:${team(m)}`;
    if (!coaches.has(k)) coaches.set(k, coachLabel(ctx, { teamId: m.events.team_id, clubId, team: team(m) }).catch(() => null));
    return coaches.get(k)!;
  };
  const infos: DerogInfo[] = await Promise.all(pres.map(async d => {
    const m = matches.find(x => x.event_id === d.event_id), pair = matches.find(x => x.event_id === d.pair_event_id);
    return infoOf(d, m, pair, { team, group: teamName, names, wait, coach: await coachOf(m), clubName, competition: competitionOf });
  }));
  return { infos, matches, names, wait, team, teamName };
}

/** Une ligne de la liste : une dérogation, la demande d'un coach à reprendre (D-061), une demande transmise au coach (D-056). */
interface Item { key: string; tab: 'out' | 'in'; chip: DerogChip; fbi: boolean; start: Date; asked: number; text: string; info?: DerogInfo; row: (sel: boolean) => Html }

export async function renderDerogations(root: HTMLElement, ctx: Ctx, sub?: string, rubric?: string): Promise<void> {
  if (sub === 'reglages') return renderDerogSettings(root, ctx, rubric ?? null);
  const wide = window.matchMedia(WIDE).matches;
  if (sub && UUID.test(sub)) {
    if (!wide) return renderDerogFiche(root, ctx, sub);
    state.sel = sub;
  }
  const club = clubOf(ctx);
  const clubId = club?.id ?? '';
  if (state.club !== clubId) {
    Object.assign(state, { club: clubId, tab: 'out', chips: { out: 'todo', in: 'todo' }, before: { out: null, in: null }, q: '', sort: 'match', sel: sub && UUID.test(sub) ? sub : null });
  }
  const teamIds = ctx.teams.filter(t => !club || t.club_id === club.id).map(t => t.id);
  const [loaded, list, pendingAll, mineAlerts, stored] = await Promise.all([load(ctx, clubId), derogations().catch(() => [] as MatchChange[]),
    pendingChanges().catch(() => [] as MatchChange[]), ffbbAlertsMine().catch(() => null), derogRangees(clubId).catch(() => new Set<string>())]);
  const { infos, names, team, teamName } = loaded;
  const now = new Date();
  const hash = location.hash;
  const reload = () => { if (location.hash === hash) void renderDerogations(root, ctx); };
  // La dérogation en cours d'un match : la plus récente (les plus anciennes d'un même match restent dans l'historique).
  const current = currentOf(infos, i => i.d.event_id);

  // Les changements de la FFBB qui attendent d'être validés (D-117 : ceux du bureau) ; les demandes des coachs (D-061).
  const ffbbPending = pendingAll.filter(c => c.source === 'ffbb' && (!mineAlerts || mineAlerts.some(a => a.change_id === c.id)));
  const fromCoach = list.filter(c => c.status === 'pending' && c.to_bureau);
  const toCoach = list.filter(c => c.status === 'pending' && !c.to_bureau);
  const extra = await matchesWithEvents([...new Set([...ffbbPending, ...fromCoach, ...toCoach].map(c => c.event_id))]).catch(() => [] as TeamMatch[]);
  const ffbb = ffbbPending.filter(c => extra.some(m => m.event_id === c.event_id && teamIds.includes(m.events.team_id)));
  const titleOf = (id: string) => { const m = extra.find(x => x.event_id === id); return m ? `${team(m)} ${m.is_home === false ? 'à' : '–'} ${m.opponent}` : 'Match'; };
  const dayOfMatch = (id: string) => { const m = extra.find(x => x.event_id === id); return m ? localDate(new Date(m.events.starts_at)) : ''; };
  const openOn = (eventId: string) => current.find(i => i.d.event_id === eventId && ['todo', 'wait', 'ffbb', 'theirs'].includes(derogChip(i.d, now, i.wait)));
  const today = localDate(now);

  // La demande d'un coach, à reprendre (« Préparer », les deux façons) ou à refuser sur la fiche du match.
  const coachRow = (c: MatchChange) => {
    const m = extra.find(x => x.event_id === c.event_id);
    const who = c.requested_by ? names.get(c.requested_by) ?? 'le coach' : 'le coach';
    return html`<li class="dg-row">
      <a class="fm-main" href="#/e/${c.event_id}/${dayOfMatch(c.event_id)}"><span class="dg-kind">Demandée par ${who} · à reprendre</span><strong>${titleOf(c.event_id)}</strong>
        <span class="muted small">${c.kind === 'swap' ? `Inversion domicile / extérieur${c.new_starts_at && c.old_starts_at && c.new_starts_at !== c.old_starts_at ? ` : ce match le ${dayHm(c.new_starts_at)}` : ''}${c.pair_new_starts_at ? `, le retour le ${dayHm(c.pair_new_starts_at)}` : ''}`
          : `${c.old_starts_at ? dayHm(c.old_starts_at) : '—'} → ${c.new_starts_at ? dayHm(c.new_starts_at) : '—'}${c.new_location && c.new_location !== c.old_location ? ` · ${c.new_location}` : ''}`}</span></a>
      ${stepBar(['now', '', '', ''], ['Pré-demande', 'Réponse', 'Saisie FBI', 'FFBB'])}
      <p class="dg-next">${c.note ? `« ${c.note} » ` : ''}<strong>À toi</strong> : prépare-la pour ${m?.opponent ?? 'l’autre club'}, ou réponds au coach sur la fiche du match.</p>
      <div class="btn-row"><button type="button" class="btn btn-primary btn-sm" data-coachreq="${c.id}">Préparer</button>
        <a class="btn btn-link btn-sm" href="#/e/${c.event_id}/${dayOfMatch(c.event_id)}">Refuser…</a></div>
    </li>`;
  };
  // Les demandes transmises au coach (D-056) qui attendent encore sa réponse.
  const legacyRow = (c: MatchChange) => html`<li class="dg-row">
    <a class="fm-main" href="#/e/${c.event_id}/${dayOfMatch(c.event_id)}"><span class="dg-kind">Transmise au coach</span><strong>${titleOf(c.event_id)}</strong>
      <span class="muted small">${c.kind === 'swap' ? 'Inversion domicile / extérieur' : `${when(c.old_starts_at)} → ${when(c.new_starts_at)}`}</span></a>
    <p class="dg-next mute">Chez le coach : il accepte ou refuse sur la fiche du match.</p></li>`;

  // --- Les lignes, rangées par onglet et par puce --------------------------------------------------------------------
  const textOf = (i: DerogInfo) => searchKey(`${i.title} ${i.opponent} ${i.d.opponent_club} ${i.team} ${i.group}`);
  const items: Item[] = current.filter(i => !stored.has(i.d.id)).map(i => ({
    key: i.d.id, tab: i.d.direction === 'in' ? 'in' : 'out', chip: derogChip(i.d, now, i.wait), fbi: fbiNow(i.d, now, i.wait), start: i.start,
    asked: new Date(i.d.received_at ?? i.d.created_at).getTime(), text: textOf(i), info: i, row: (sel: boolean) => derogRow(i, now, sel),
  }));
  const matchOf = (c: MatchChange) => extra.find(x => x.event_id === c.event_id);
  for (const c of fromCoach) {
    const m = matchOf(c);
    if (!m || localDate(new Date(m.events.starts_at)) < today) continue;
    items.push({ key: `c:${c.id}`, tab: 'out', chip: 'todo', fbi: false, start: new Date(m.events.starts_at), asked: new Date(c.detected_at).getTime(),
      text: searchKey(`${titleOf(c.event_id)} ${m.opponent} ${teamName(m.events.team_id)}`), row: () => coachRow(c) });
  }
  for (const c of toCoach) {
    const m = matchOf(c);
    if (!m || localDate(new Date(m.events.starts_at)) < today) continue;
    items.push({ key: `l:${c.id}`, tab: 'out', chip: 'wait', fbi: false, start: new Date(m.events.starts_at), asked: new Date(c.detected_at).getTime(),
      text: searchKey(`${titleOf(c.event_id)} ${m.opponent} ${teamName(m.events.team_id)}`), row: () => legacyRow(c) });
  }
  const rangees: Item[] = current.filter(i => stored.has(i.d.id)).map(i => ({
    key: i.d.id, tab: i.d.direction === 'in' ? 'in' : 'out', chip: derogChip(i.d, now, i.wait), fbi: false, start: i.start,
    asked: new Date(i.d.received_at ?? i.d.created_at).getTime(), text: textOf(i), info: i, row: (sel: boolean) => derogRow(i, now, sel),
  }));
  const inChip = (it: Item, c: Chip) => c === 'all' || (c === 'fbi' ? it.fbi : it.chip === c);

  // --- La page ---------------------------------------------------------------------------------------------------------
  const noDerog = ffbb.every(c => !openOn(c.event_id));
  const ffbbHead = html`<span class="dz-ffbb-ic" aria-hidden="true">!</span><span class="card-main"><strong>${ffbb.length} changement${ffbb.length > 1 ? 's' : ''} détecté${ffbb.length > 1 ? 's' : ''} sur FFBB${noDerog ? ', sans dérogation' : ''}</strong>
    <span class="small muted">À valider · ${ffbb.map(c => titleOf(c.event_id)).join(', ')}</span></span><span class="occ-go" aria-hidden="true">›</span>`;
  const ffbbRow = (c: MatchChange) => html`<li>
      <a class="fm-main" href="#/e/${c.event_id}/${dayOfMatch(c.event_id)}"><strong>${titleOf(c.event_id)}</strong>
        <span class="muted small">${c.kind === 'removed' ? 'Retiré du calendrier FFBB' : `${when(c.old_starts_at)} → ${when(c.new_starts_at)}${c.new_location && c.new_location !== c.old_location ? ` · ${c.new_location}` : ''}`}</span>
        ${openOn(c.event_id) ? html`<span class="small">Dérogation en cours : ce n’est pas le créneau demandé.</span>` : ''}</a>
      <span class="chip chip-warn">À valider</span></li>`;
  const banner = !ffbb.length ? ''
    : ffbb.length === 1 ? html`<a class="dz-ffbb" href="#/e/${ffbb[0]!.event_id}/${dayOfMatch(ffbb[0]!.event_id)}" data-guide="dg-ffbb">${ffbbHead}</a>`
    : html`<details class="dz-ffbb-all" data-guide="dg-ffbb"><summary class="dz-ffbb">${ffbbHead}</summary><ul class="ce-rows fm-list">${ffbb.map(ffbbRow)}</ul></details>`;
  const head = html`<header class="screen-head"><p class="eyebrow">Bureau · matchs</p><h2>Dérogations</h2>
    <p class="muted">Chaque dérogation en 4 étapes, de la pré-demande à la confirmation sur FFBB.</p></header>`;
  const two2 = html`<div class="dg-two"><button type="button" class="btn btn-primary" id="dg-new" data-guide="dg-new">Nouvelle demande</button>
    <button type="button" class="btn btn-ghost" id="dg-in" data-guide="dg-in">Demande reçue</button></div>`;
  const seg = html`<nav class="seg" aria-label="Feuilles et dérogations"><a href="#/feuilles">Feuilles de match</a><a href="#/derogations" aria-current="page">Dérogations</a></nav>`;
  const tools = html`<div class="dz-tools" data-guide="dg-filters">
      <div id="dz-tabs"></div>
      <input type="search" class="pick-search" id="dz-q" placeholder="Chercher une équipe, un adversaire" aria-label="Chercher une demande" autocomplete="off" value="${state.q}">
      <div id="dz-chips"></div>
      <div id="dz-sort"></div></div>
    <div id="dz-body"></div>`;
  const empty = !current.length && !ffbb.length && !fromCoach.length && !toCoach.length;
  const emptyHtml = html`<div class="empty"><p class="empty-title">Aucune dérogation pour l’instant.</p>
    <p class="muted">« Nouvelle demande » : un petit changement, tu préviens l’autre club ; sinon tu lui demandes son accord. Il répond en un clic, sans compte ; tu peux saisir sur FBI sans attendre.</p></div>`;
  mount(root, wide
    ? html`<section class="screen dz-wide"><a class="back" href="#/club">‹ Le club</a><div class="dz-top">${head}${two2}</div>${seg}${banner}
        ${empty ? emptyHtml : html`<div class="dz-split"><div class="dz-list">${tools}</div><aside class="dz-detail" id="dz-detail" aria-label="La demande choisie"></aside></div>`}</section>`
    : html`<section class="screen"><a class="back" href="#/club">‹ Le club</a>${head}${seg}${two2}${banner}${empty ? emptyHtml : tools}</section>`);

  // --- Le corps : onglets, puces, tri, lignes (repeints sans toucher à la recherche) ----------------------------------
  const sortLabel = (s: 'match' | 'asked') => (s === 'match' ? 'Par date du match' : 'Par date de la demande');
  const group = (list: Item[], sel: string | null) => {
    if (state.sort === 'asked') return html`${list.map(it => it.row(it.key === sel))}`;
    let last = '';
    return html`${list.map(it => {
      const w = weekendLabel(it.start.toISOString(), now);
      const sep = w !== last ? html`<li class="dz-sep">${w}</li>` : '';
      last = w;
      return html`${sep}${it.row(it.key === sel)}`;
    })}`;
  };
  const order = (list: Item[], desc = false) => [...list].sort((a, b) => (state.sort === 'asked' ? b.asked - a.asked : (desc ? -1 : 1) * (a.start.getTime() - b.start.getTime())));
  const paint = () => {
    if (empty) return;
    const q = searchKey(state.q);
    const hit = (it: Item) => !q || it.text.includes(q);
    const tabItems = (t: 'out' | 'in') => items.filter(it => it.tab === t && hit(it));
    const byTab = { out: tabItems('out'), in: tabItems('in') };
    const chipsOf = (t: 'out' | 'in') => (t === 'out' ? OUT_CHIPS : IN_CHIPS);
    for (const t of ['out', 'in'] as const) if (!chipsOf(t).some(c => c[0] === state.chips[t]) && state.chips[t] !== 'stored') state.chips[t] = 'todo';
    const todo = (xs: Item[]) => xs.filter(it => it.chip === 'todo').length;
    // Les onglets (data-phase / data-pane : une visite guidée ouvre l'onglet qui contient l'élément qu'elle cherche).
    mount($(root, '#dz-tabs')!, html`<nav class="seg dz-tabs" aria-label="Nos demandes et demandes reçues">
      ${(['out', 'in'] as const).map(t => { const xs = byTab[t]; const n = todo(xs); return html`
        <button type="button" data-tab="${t}" data-phase="${t}" aria-pressed="${String(state.tab === t)}"><span>${t === 'out' ? 'Nos demandes' : 'Demandes reçues'}</span>
          <small>${xs.length}${n ? html` · <em>${n} à faire</em>` : ''}</small></button>`; })}</nav>`);
    const chip = state.chips[state.tab], mine = byTab[state.tab];
    mount($(root, '#dz-chips')!, html`<nav class="plk-filter dz-chips" aria-label="État">${chipsOf(state.tab).map(([k, l]) => {
      const n = mine.filter(it => inChip(it, k)).length;
      return html`<button type="button" data-chip="${k}" aria-pressed="${String(k === chip)}" class="${n ? '' : 'dz-zero'}">${l}<span class="n">${n}</span></button>`;
    })}</nav>`);
    const sel = wide ? state.sel : null;
    /** Le corps d'un onglet : sa puce, la recherche, les week-ends ; « Rangées » au bout. */
    const pane = (t: 'out' | 'in') => {
      const c = state.chips[t], xs = byTab[t];
      const storedMine = rangees.filter(it => it.tab === t);
      const storedHit = storedMine.filter(hit);
      const shown = c === 'stored' ? order(storedHit, true) : order(xs.filter(it => inChip(it, c)));
      let body: Html;
      if (c === 'stored') {
        body = html`${shown.length ? html`<ul class="dg-rows">${group(shown, sel)}</ul>` : html`<p class="dz-empty-chip">${q ? `Aucune demande rangée pour « ${state.q.trim()} ».` : 'Aucune demande rangée.'}</p>`}
          <div class="dz-end"><button type="button" class="btn btn-ghost btn-sm dz-back" data-chip="todo">‹ Les demandes en cours</button></div>`;
      } else if (c === 'all') {
        const live = shown.filter(it => it.chip !== 'ok' && it.chip !== 'ko'), done = shown.filter(it => it.chip === 'ok' || it.chip === 'ko');
        body = html`${live.length ? html`<ul class="dg-rows">${group(live, sel)}</ul>` : ''}
          ${done.length ? html`<details class="block dg-fold" ${q ? html`open` : ''}><summary><h3>Terminées · ${done.length}</h3></summary><ul class="dg-rows">${group(done, sel)}</ul></details>` : ''}
          ${q && storedHit.length ? html`<details class="block dg-fold" open><summary><h3>Rangées · ${storedHit.length}</h3></summary><ul class="dg-rows">${group(order(storedHit, true), sel)}</ul></details>` : ''}
          ${!live.length && !done.length && !(q && storedHit.length) ? html`<p class="dz-empty-chip">${q ? `Aucune demande pour « ${state.q.trim()} ».` : 'Rien ici pour l’instant.'}</p>` : ''}`;
      } else {
        body = shown.length ? html`<ul class="dg-rows">${group(shown, sel)}</ul>`
          : html`<p class="dz-empty-chip">${q ? `Aucune demande pour « ${state.q.trim()} » ici.` : c === 'todo' ? 'Rien à faire maintenant.' : 'Rien ici pour l’instant.'}</p>`;
      }
      const storeCard = storedMine.length ? html`<div class="dz-end"><button type="button" class="dz-store" data-chip="stored"><span>Rangées · ${storedMine.length}
        <small>Le match est joué : elles quittent la liste le lendemain du match.</small></span><span class="occ-go" aria-hidden="true">›</span></button></div>` : '';
      const end = c === 'ok' || c === 'ko' || c === 'all' ? storeCard
        : c === 'todo' ? html`<p class="hint dz-end">Les terminées restent dans « Confirmées » et « Refusées ou abandonnées » jusqu’au match, puis sont rangées.</p>` : '';
      return { shown, html: html`${body}${end}` };
    };
    const panes = { out: pane('out'), in: pane('in') };
    const shown = panes[state.tab].shown;
    const label = chip === 'stored' ? 'Rangées' : chipsOf(state.tab).find(c => c[0] === chip)![1];
    mount($(root, '#dz-sort')!, html`<p class="dz-sort"><span>${q ? html`${shown.length} résultat${shown.length > 1 ? 's' : ''} pour « ${state.q.trim()} »` : html`<b>${label}</b> · ${shown.length}`}</span>
      <select id="dz-order" aria-label="Trier">${(['match', 'asked'] as const).map(s => html`<option value="${s}" ${state.sort === s ? html`selected` : ''}>${sortLabel(s)}</option>`)}</select></p>`);
    const bodyEl = $(root, '#dz-body')!;
    mount(bodyEl, html`${(['out', 'in'] as const).map(t => html`<div class="dz-pane" data-pane="${t}" ${t === state.tab ? '' : html`hidden`}>${panes[t].html}</div>`)}`);
    wireBody(bodyEl);
    // La puce choisie reste visible dans la barre qui défile (téléphone).
    const nav = $(root, '.dz-chips'), on = $<HTMLElement>(root, '.dz-chips [aria-pressed="true"]');
    if (nav && on && !wide) nav.scrollLeft = Math.max(0, on.offsetLeft - nav.offsetLeft - 40);
    if (wide) showDetail(shown);
  };

  // --- Les gestes ----------------------------------------------------------------------------------------------------
  const toTarget = (m: TeamMatch): PreTarget => ({
    eventId: m.event_id, teamId: m.events.team_id, clubId: ctx.teams.find(t => t.id === m.events.team_id)?.club_id ?? clubId,
    start: new Date(m.events.starts_at), location: m.events.location, team: team(m), opponent: m.opponent, isHome: m.is_home, competition: competitionOf(m),
  });
  const again = (i: DerogInfo, o?: { accord: boolean }) => {
    const m = loaded.matches.find(x => x.event_id === i.d.event_id);
    if (!m) { toast('Match introuvable.', 'ko'); return; }
    const s = i.d.slots[0];
    const at = s ? new Date(s.starts_at) : null;
    openDerogPreSheet(ctx, toTarget(m), reload, {
      email: i.d.opponent_email, club: i.d.opponent_club, reason: i.d.reason, note: i.d.reason_note, mode: 'accord',
      // « Ça nous pose problème » → « Demander leur accord » : le créneau annoncé, prérempli.
      slots: o?.accord && s && at ? [{ date: localDate(at), time: `${two(at.getHours())}:${two(at.getMinutes())}`, location: s.location ?? '' }] : undefined,
    });
  };
  const wireBody = (el: HTMLElement) => {
    wireDerogActions(el, infos, reload, again);
    for (const b of $$<HTMLButtonElement>(el, '[data-chip]')) b.addEventListener('click', () => {
      state.chips[state.tab] = b.dataset.chip as Chip; state.before[state.tab] = null; paint();
      $(root, '.dz-tools')?.scrollIntoView({ block: 'start' });
    });
    for (const b of $$<HTMLButtonElement>(el, '[data-coachreq]')) b.addEventListener('click', () => {
      const c = fromCoach.find(x => x.id === b.dataset.coachreq)!;
      const m = extra.find(x => x.event_id === c.event_id);
      if (!m) { toast('Match introuvable.', 'ko'); return; }
      const at = c.new_starts_at ? new Date(c.new_starts_at) : null;
      const pre: PrePrefill = {
        coach: { change: c.id, by: `${c.requested_by ? names.get(c.requested_by) ?? 'le coach' : 'le coach'}, coach ${team(m)}`, at: c.detected_at, note: c.note },
        kind: c.kind === 'swap' ? 'swap' : 'moved',
        slots: c.kind === 'moved' && at ? [{ date: localDate(at), time: `${two(at.getHours())}:${two(at.getMinutes())}`, location: c.new_location ?? '' }] : undefined,
        swapDates: c.kind === 'swap' ? { start: c.new_starts_at, pairStart: c.pair_new_starts_at } : undefined,
        note: c.note,
      };
      openDerogPreSheet(ctx, toTarget(m), reload, pre);
    });
    // Version large : une ligne touchée s'ouvre à droite, sans quitter la liste.
    if (wide) for (const a of $$<HTMLAnchorElement>(el, 'a[data-open]')) a.addEventListener('click', e => {
      e.preventDefault();
      state.sel = a.dataset.open!;
      $$(root, '.dg-row.dz-sel').forEach(r => r.classList.remove('dz-sel'));
      a.closest('.dg-row')?.classList.add('dz-sel');
      showDetail(null);
    });
  };
  const showDetail = (shown: Item[] | null) => {
    const pane = $(root, '#dz-detail');
    if (!pane) return;
    const all = [...items, ...rangees];
    let it = all.find(x => x.key === state.sel && x.info);
    if (!it && shown) { it = shown.find(x => x.info); state.sel = it?.key ?? null; $(root, `.dg-row[data-dg="${state.sel}"]`)?.classList.add('dz-sel'); }
    const i = it?.info ?? (state.sel ? infos.find(x => x.d.id === state.sel) : undefined);
    if (!i) { mount(pane, html`<p class="dz-detail-empty">Choisis une demande à gauche : sa fiche s’affiche ici, avec le texte à reporter dans FBI.</p>`); return; }
    mount(pane, derogFiche(i, now, true));
    $$(pane, '.screen-head').forEach(h => h.classList.add('nv-has-gear'));
    wireDerogActions(pane, [i], reload, again);
    seenAndHall(pane, ctx, i);
  };

  if (!empty) {
    for (const t of $$<HTMLElement>(root, '#dz-tabs')) t.addEventListener('click', e => {
      const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-tab]');
      if (!b) return;
      state.tab = b.dataset.tab === 'in' ? 'in' : 'out';
      paint();
    });
    $(root, '#dz-chips')!.addEventListener('click', e => {
      const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-chip]');
      if (!b) return;
      state.chips[state.tab] = b.dataset.chip as Chip; state.before[state.tab] = null;
      paint();
    });
    $(root, '#dz-sort')!.addEventListener('change', e => {
      const s = e.target as HTMLSelectElement;
      if (s.id === 'dz-order') { state.sort = s.value === 'asked' ? 'asked' : 'match'; paint(); }
    });
    // La recherche passe sur « Toutes » (les demandes de l'onglet, tous états) ; effacée, on revient à la puce d'avant.
    $<HTMLInputElement>(root, '#dz-q')!.addEventListener('input', e => {
      const v = (e.target as HTMLInputElement).value;
      for (const t of ['out', 'in'] as const) {
        if (v.trim() && !state.q.trim() && state.chips[t] !== 'all' && state.chips[t] !== 'stored') { state.before[t] = state.chips[t]; state.chips[t] = 'all'; }
        if (!v.trim() && state.before[t]) { state.chips[t] = state.before[t]!; state.before[t] = null; }
      }
      state.q = v;
      paint();
    });
    paint();
  }
  $(root, '#dg-in')!.addEventListener('click', () => void openRecueSheet(ctx, { clubId }, reload));
  $(root, '#dg-new')!.addEventListener('click', () => void pickMatch(ctx, teamIds, clubId, team, teamName, toTarget, reload));

  // La version large se recompose quand la fenêtre passe les 1000 px.
  wideWatch?.mq.removeEventListener('change', wideWatch.fn);
  const mq = window.matchMedia(WIDE);
  const fn = () => { if (location.hash === hash && root.isConnected) void renderDerogations(root, ctx, sub); };
  mq.addEventListener('change', fn);
  wideWatch = { mq, fn };
}

/** « Nouvelle demande » : sur quel match ? (Idries : « pouvoir chercher, filtrer pour retrouver les matchs : par équipe, par
 *  mois, week-end ») : une recherche, la période (ce week-end, le suivant, chaque mois) et l'équipe ; filtrage sur place. */
async function pickMatch(ctx: Ctx, teamIds: string[], clubId: string, team: (m: TeamMatch) => string, teamName: (id: string) => string,
  toTarget: (m: TeamMatch) => PreTarget, reload: () => void): Promise<void> {
  const up = await clubUpcomingMatches(teamIds, 120).catch(() => [] as TeamMatch[]);
  const dayOf = (m: TeamMatch) => localDate(new Date(m.events.starts_at));
  const today = localDate(new Date()), dow = new Date().getDay();
  const sat0 = addDays(today, dow === 0 ? -1 : 6 - dow);
  const months = [...new Set(up.map(m => dayOf(m).slice(0, 7)))].sort();
  const monthLabel = (ym: string) => new Date(`${ym}-01T12:00:00`).toLocaleDateString('fr-FR', { month: 'long' }).replace(/^./, c => c.toUpperCase());
  const groups = [...new Map(up.map(m => [m.events.team_id, teamName(m.events.team_id)])).entries()].sort((a, b) => a[1].localeCompare(b[1], 'fr'));
  const periods: [string, string][] = [['', 'Tout'], ['w0', 'Ce week-end'], ['w1', 'Week-end prochain'], ...months.map(ym => [`m${ym}`, monthLabel(ym)] as [string, string])];
  const inPeriod = (m: TeamMatch, f: string) => !f || (f === 'w0' ? [sat0, addDays(sat0, 1)].includes(dayOf(m))
    : f === 'w1' ? [addDays(sat0, 7), addDays(sat0, 8)].includes(dayOf(m)) : dayOf(m).startsWith(f.slice(1)));
  const title = (m: TeamMatch) => `${team(m)} ${m.is_home === false ? 'à' : '–'} ${m.opponent}`;
  openSheet('Sur quel match ?', up.length ? html`
    <div class="pick-tools">
      <input type="search" class="pick-search" placeholder="Chercher une équipe, un adversaire" aria-label="Chercher un match" autocomplete="off">
      <nav class="plk-filter" aria-label="Période">${periods.map(([k, l]) => html`<button type="button" data-per="${k}" aria-pressed="${String(!k)}">${l}</button>`)}</nav>
      ${groups.length > 1 ? html`<nav class="plk-filter plk-teams" aria-label="Équipe"><button type="button" data-grp="" aria-pressed="true">Toutes les équipes</button>${groups.map(([id, n]) => html`<button type="button" data-grp="${id}" aria-pressed="false">${n}</button>`)}</nav>` : ''}
    </div>
    <ul class="pick-list">${up.map(m => html`<li data-pick="${m.event_id}"><button type="button" data-m="${m.event_id}">
      <span class="pick-main"><strong>${title(m)}</strong><span class="muted small">${when(m.events.starts_at)}${m.events.location ? ` · ${m.events.location}` : ''}</span></span>
      <span class="chip chip-ok">Choisir</span></button></li>`)}</ul>
    <p class="muted pick-none" hidden>Aucun match pour ce choix.</p>` : html`<p class="muted">Aucun match à venir.</p>`, (sheet, close) => {
    let per = '', grp = '', q = '';
    const apply = () => {
      let shown = 0;
      for (const m of up) {
        const li = sheet.querySelector<HTMLElement>(`[data-pick="${m.event_id}"]`);
        if (!li) continue;
        const ok = inPeriod(m, per) && (!grp || m.events.team_id === grp) && (!q || searchKey(`${title(m)} ${m.events.location ?? ''}`).includes(q));
        li.hidden = !ok; if (ok) shown++;
      }
      const none = sheet.querySelector<HTMLElement>('.pick-none'); if (none) none.hidden = shown > 0;
    };
    sheet.querySelector<HTMLInputElement>('.pick-search')?.addEventListener('input', e => { q = searchKey((e.target as HTMLInputElement).value); apply(); });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-per]')) b.addEventListener('click', () => {
      per = b.dataset.per ?? ''; $$(sheet, '[data-per]').forEach(x => x.setAttribute('aria-pressed', String(x === b))); apply();
    });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-grp]')) b.addEventListener('click', () => {
      grp = b.dataset.grp ?? ''; $$(sheet, '[data-grp]').forEach(x => x.setAttribute('aria-pressed', String(x === b))); apply();
    });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-m]')) b.addEventListener('click', () => {
      const m = up.find(x => x.event_id === b.dataset.m)!;
      close();
      openDerogPreSheet(ctx, toTarget(m), reload, {}, () => void openRecueSheet(ctx, { eventId: m.event_id, clubId }, reload));
    });
  });
}

/** Une fiche ouverte : la carte verte de l'accueil ne revient plus ; la salle demandée par l'autre club, vérifiée. */
function seenAndHall(root: HTMLElement, ctx: Ctx, i: DerogInfo): void {
  const d = i.d;
  if (d.direction === 'in' && d.kind === 'moved' && d.slots[0] && i.isHome !== false) {
    void showHall($(root, '#dg-hall'), d.club_id, d.slots[0].location ?? i.location, new Date(d.slots[0].starts_at),
      { exclude: [d.event_id], teamName: (t: string) => ctx.teams.find(x => x.id === t)?.name ?? '', squadName: (q: string | null) => squadName(ctx, q) });
  }
  if (d.status === 'confirmed' && !d.seen_by.includes(ctx.uid)) void derogSeen(d.id).catch(() => undefined);
}

/** d2, d4c, p5 : la fiche d'une dérogation (téléphone). */
async function renderDerogFiche(root: HTMLElement, ctx: Ctx, id: string): Promise<void> {
  const d = await derogPre(id).catch(() => null);
  if (!d) {
    mount(root, html`<section class="screen"><a class="back" href="#/derogations">‹ Dérogations</a>
      <div class="empty"><p class="empty-title">Dérogation introuvable.</p><p class="muted">Elle a peut-être été retirée.</p></div></section>`);
    return;
  }
  const clubId = d.club_id;
  const { infos, matches, team } = await load(ctx, clubId, [d]);
  const i = infos[0]!;
  const hash = location.hash;
  const reload = () => { if (location.hash === hash) void renderDerogFiche(root, ctx, id); };
  mount(root, derogFiche(i));
  const again = (_: DerogInfo, o?: { accord: boolean }) => {
    const m = matches.find(x => x.event_id === d.event_id);
    if (!m) return;
    const s = d.slots[0], at = s ? new Date(s.starts_at) : null;
    openDerogPreSheet(ctx, { eventId: m.event_id, teamId: m.events.team_id, clubId, start: new Date(m.events.starts_at), location: m.events.location, team: team(m),
      opponent: m.opponent, isHome: m.is_home, competition: competitionOf(m) }, () => { location.hash = '#/derogations'; }, {
      email: d.opponent_email, club: d.opponent_club, reason: d.reason, note: d.reason_note, mode: 'accord',
      slots: o?.accord && s && at ? [{ date: localDate(at), time: `${two(at.getHours())}:${two(at.getMinutes())}`, location: s.location ?? '' }] : undefined,
    });
  };
  wireDerogActions(root, [i], reload, again);
  seenAndHall(root, ctx, i);
}

/** Les réglages des dérogations (D-139 : la ⚙ de la page Dérogations ouvre une page en rubriques) : le délai « sans
 *  réponse », un rappel, jamais un verrou. */
async function renderDerogSettings(root: HTMLElement, ctx: Ctx, rubric: string | null): Promise<void> {
  const clubId = clubOf(ctx)?.id ?? '';
  const admin = ctx.adminOf.some(c => c.id === clubId);
  const render = async (): Promise<void> => {
    const wait = await derogWaitDays(clubId).catch(() => 3);
    settingsPage(root, {
      key: 'derogations', intro: 'Ce qui guide le suivi des dérogations du club.', open: rubric, openByDefault: ['sans-reponse'],
      rubrics: [{
        key: 'sans-reponse', title: 'Sans réponse de l’autre club', sum: `au bout de ${wait} jours`, icon: 'ti-cal', guide: 'dg-wait',
        body: (el, again) => {
          mount(el, html`<div class="field"><span>Au bout de</span>
              <div class="seg dg-days" role="radiogroup" aria-label="Délai sans réponse">${[2, 3, 4, 5, 6, 7].map(n => html`
                <button type="button" role="radio" data-days="${n}" aria-pressed="${String(n === wait)}" ${admin ? '' : html`disabled`}>${n} j</button>`)}</div></div>
            <p class="hint">Ce n’est qu’un rappel : l’étape « Réponse » passe en hachuré « sans réponse » et « Relancer » est proposé. La saisie sur FBI n’attend jamais la réponse ; si l’autre club répond plus tard, c’est noté. « Les prévenir » n’attend rien : ni délai, ni relance.</p>`);
          for (const b of $$<HTMLButtonElement>(el, '[data-days]')) b.addEventListener('click', async () => {
            const n = Number(b.dataset.days);
            try { await setDerogWaitDays(clubId, n); toast(`Sans réponse au bout de ${n} jours`); again(); }
            catch { toast('Impossible pour le moment.', 'ko'); }
          });
        },
      }],
    }, render);
  };
  await render();
}
